<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import api from "../api.ts";
import {useMessagesStore} from "../stores/messages.ts";
import {copyToClipboard} from "../utils";

type OpenIdConfiguration = Record<string, string | string[] | boolean | number | null | Record<string, unknown>>;

const messages = useMessagesStore();

const primaryConfigKeys = [
  'issuer',
  'authorization_endpoint',
  'token_endpoint',
  'userinfo_endpoint',
  'jwks_uri',
] as const;

const openIdDialog = ref(false);
const openIdConfig = ref<OpenIdConfiguration | null>(null);
const openIdConfigLoading = ref(false);
const openIdConfigError = ref<string | null>(null);
const hasLoadedOpenIdConfiguration = ref(false);

const isOpenIdConfiguration = (value: unknown): value is OpenIdConfiguration => (
    typeof value === 'object' && value !== null && !Array.isArray(value)
);

const openIdPrimaryFields = computed(() => {
  if (!isOpenIdConfiguration(openIdConfig.value)) return [];

  return primaryConfigKeys
      .filter((key) => key in openIdConfig.value)
      .map((key) => ({
        key,
        value: openIdConfig.value[key]
      }));
});

const openIdAdditionalFields = computed(() => {
  if (!isOpenIdConfiguration(openIdConfig.value)) return [];

  return Object.entries(openIdConfig.value)
      .filter(([key]) => !primaryConfigKeys.includes(key as typeof primaryConfigKeys[number]))
      .map(([key, value]) => ({key, value}));
});

const openIdRawJson = computed(() => (
    openIdConfig.value ? JSON.stringify(openIdConfig.value, null, 2) : ''
));

const formatConfigLabel = (key: string): string => (
    key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
);

const formatConfigValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value, null, 2);
  if (value === null) return '—';
  return String(value);
};

const fetchOpenIdConfiguration = async (): Promise<void> => {
  try {
    openIdConfigLoading.value = true;
    openIdConfigError.value = null;
    const response = await api.get('/.well-known/openid-configuration');

    if (!isOpenIdConfiguration(response.data)) {
      openIdConfig.value = null;
      openIdConfigError.value = 'Received an unexpected response for the OpenID configuration.';
      return;
    }

    openIdConfig.value = response.data;
    hasLoadedOpenIdConfiguration.value = true;
  } catch (error) {
    console.error(error);
    openIdConfig.value = null;
    openIdConfigError.value = 'Unable to load the OpenID configuration.';
  } finally {
    openIdConfigLoading.value = false;
  }
};

const openDialog = async (): Promise<void> => {
  openIdDialog.value = true;

  if (!hasLoadedOpenIdConfiguration.value && !openIdConfigLoading.value) {
    await fetchOpenIdConfiguration();
  }
};

const copyRawOpenIdConfiguration = (): void => {
  if (!openIdRawJson.value) return;

  copyToClipboard(openIdRawJson.value);
  messages.add({
    text: 'OpenID configuration JSON copied to clipboard.',
    color: 'info'
  });
};

onMounted(async () => {
  await fetchOpenIdConfiguration();
});
</script>

<template>
  <v-btn
      color="primary"
      variant="tonal"
      prepend-icon="mdi-openid"
      @click="openDialog"
  >
    OpenID Config
  </v-btn>

  <v-dialog v-model="openIdDialog" max-width="1100">
    <v-card rounded="xl" elevation="1" class="openid-card openid-dialog-card">
      <v-card-item class="openid-card-header">
        <template #prepend>
          <v-avatar color="primary" variant="tonal" size="40">
            <v-icon color="primary" icon="mdi-openid"/>
          </v-avatar>
        </template>
        <v-card-title>OpenID Configuration</v-card-title>
        <v-card-subtitle>
          Discovery document served by this GenAuth instance
        </v-card-subtitle>
        <template #append>
          <div class="d-flex align-center flex-wrap ga-2">
            <v-chip
                color="primary"
                variant="tonal"
                prepend-icon="mdi-file-code-outline"
            >
              /.well-known/openid-configuration
            </v-chip>
            <v-btn
                variant="tonal"
                color="primary"
                prepend-icon="mdi-content-copy"
                :disabled="!openIdRawJson"
                @click="copyRawOpenIdConfiguration"
            >
              Copy Raw JSON
            </v-btn>
          </div>
        </template>
      </v-card-item>

      <v-card-text class="openid-card-body">
        <v-progress-linear
            v-if="openIdConfigLoading"
            indeterminate
            color="primary"
            class="mb-4"
        />

        <v-alert
            v-else-if="openIdConfigError"
            type="error"
            variant="tonal"
            class="mb-4"
        >
          {{ openIdConfigError }}
        </v-alert>

        <template v-else-if="openIdConfig">
          <v-row dense>
            <v-col
                v-for="field in openIdPrimaryFields"
                :key="field.key"
                cols="12"
                md="6"
            >
              <v-card variant="outlined" rounded="lg" class="fill-height openid-field-card">
                <v-card-text>
                  <div class="text-overline text-primary">{{ formatConfigLabel(field.key) }}</div>
                  <div class="text-body-1 text-break">{{ formatConfigValue(field.value) }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-card variant="outlined" rounded="lg" class="mt-4">
            <v-card-item>
              <v-card-title class="text-subtitle-1">Additional Metadata</v-card-title>
            </v-card-item>
            <v-card-text class="pt-0">
              <v-table density="comfortable">
                <thead>
                <tr>
                  <th class="text-left">Property</th>
                  <th class="text-left">Value</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="field in openIdAdditionalFields" :key="field.key">
                  <td class="font-weight-medium">{{ formatConfigLabel(field.key) }}</td>
                  <td class="text-body-2">
                    <div class="text-pre-wrap">{{ formatConfigValue(field.value) }}</div>
                  </td>
                </tr>
                </tbody>
              </v-table>
            </v-card-text>
          </v-card>

<!--          <v-expansion-panels variant="accordion" class="mt-4">-->
<!--            <v-expansion-panel>-->
<!--              <v-expansion-panel-title>Raw JSON Preview</v-expansion-panel-title>-->
<!--              <v-expansion-panel-text>-->
<!--                <pre class="openid-config-json">{{ openIdRawJson }}</pre>-->
<!--              </v-expansion-panel-text>-->
<!--            </v-expansion-panel>-->
<!--          </v-expansion-panels>-->
        </template>
      </v-card-text>

      <v-card-actions class="px-6 pb-6 openid-card-footer">
        <v-spacer/>
        <v-btn variant="text" @click="openIdDialog = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.openid-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.openid-dialog-card {
  display: flex;
  flex-direction: column;
  max-height: min(85vh, 900px);
}

.openid-card-header,
.openid-card-footer {
  flex: 0 0 auto;
}

.openid-card-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.openid-field-card {
  border-color: rgba(var(--v-theme-primary), 0.16);
}

.openid-config-json {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.875rem;
  line-height: 1.5;
}

.text-pre-wrap {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
